// src/api/campaignApi.ts
import axios from 'axios';
import config from '../config';
import { store } from '../store/store';
import { logout } from '../reducers/authSlice';
import { stepUpRequired } from '../store/mfaSlice';
import { isMfaRequired } from './errors';
import { isSessionOver } from './axiosInstance';

/**
 * Dedicated axios instance for the owner-only e-mail campaign screen.
 *
 * Same reasoning as `invoicingApi`. It was written when the shared `api` instance signed the user
 * out on ANY 403 — this screen answers 403 for reasons that have nothing to do with an expired
 * session, and being signed out of the whole site part-way through a send would be a genuinely bad
 * outcome (the send is resumable, but only if you can still reach the page).
 *
 * The shared instance no longer does that: it reads the error code out of the body, exactly as this
 * one does. So this instance is now a narrower rule rather than an opposite one — it never sees the
 * step-up traffic the shared instance carries, and it never wants the `stepUpRequired()` dispatch
 * that arm performs on a plain 403. Keeping it separate also keeps its blob-bodied responses on a
 * predicate written for them.
 *
 * A dead session is the one thing it does have to act on, and it cannot be recognised from the
 * status: the API answers a missing, expired or invalid token with 403 as well right now, so the
 * screen would sit there re-sending a token that will never work again, showing a machine code and
 * offering no way back to the login form. `isSessionOver` splits the two by reading the error code
 * out of the body, which only the auth entry point writes — so "not an admin" still reaches the
 * screen's own message, and a dead token still clears the store.
 */
const campaignApi = axios.create({
    baseURL: config.API_URL,
});

campaignApi.interceptors.request.use(
    (requestConfig) => {
        const token = store.getState().auth.accessToken;
        if (token) {
            requestConfig.headers.Authorization = `Bearer ${token}`;
        }
        return requestConfig;
    },
    (error) => Promise.reject(error)
);

campaignApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        // Session over, never a plain authorisation 403. That distinction is the whole reason this
        // instance exists, and isSessionOver draws it from the body rather than the status so it
        // keeps holding when the server goes back to answering 401.
        if (await isSessionOver(error)) {
            store.dispatch(logout());
        } else if (isMfaRequired(error)) {
            // The same arm the shared instance and `securityApi` carry. Without it this screen is
            // one of the two that answer `403 mfa_required` through their own instance and therefore
            // never reach the one modal host: with enforcement on, an admin who has not enrolled
            // would see every panel refuse and nothing offering the way out. `/admin` and
            // `/order-history` already behave that way because they go through the shared instance;
            // this is what makes campaigns and invoicing agree with them.
            store.dispatch(stepUpRequired());
        }
        return Promise.reject(error);
    }
);

export default campaignApi;
